import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../context/TranslationContext';

const SimpleImageSlider = ({
  images,
  roomName,
  discount,
  rating,
  roomsAvailable = null, // real inventory count (room.totalroom) — scarcity badge; null/0 → hide (no fake)
  onImageClick,
  className = "",
  autoplay = true,
  showControls = true
}) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const didSwipe = useRef(false); // suppress the click a swipe synthesizes

  // Auto-play functionality
  useEffect(() => {
    if (!autoplay || !images || images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide(prevIndex => (prevIndex + 1) % images.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [images, autoplay]);

  // If no images, show fallback
  if (!images || images.length === 0) {
    return (
      <div className={`relative w-full h-48 bg-gray-200 flex items-center justify-center ${className}`}>
        <div className="text-gray-500 text-center">
          <svg className="h-12 w-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
          </svg>
          <p className="text-sm">No images available</p>
        </div>
      </div>
    );
  }

  const nextSlide = () => {
    setCurrentSlide((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Touch swipe — horizontal swipe se next/prev (vertical scroll ignore)
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    // horizontal swipe (≥40px) aur vertical se zyada ho tabhi
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      didSwipe.current = true; // suppress the synthesized click that follows
      setTimeout(() => { didSwipe.current = false; }, 350);
      if (dx < 0) nextSlide(); else prevSlide();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  // Tap opens the gallery at the currently-shown image — but ignore the click a swipe synthesizes
  const handleImageClick = () => {
    if (didSwipe.current) return;
    if (onImageClick) onImageClick(currentSlide);
  };

  return (
    <div className={`relative w-full h-48 md:h-64 lg:h-50 rounded-t-lg overflow-hidden ${className}`}>
      {/* Main Image Container */}
      <div
        className="relative w-full h-full select-none"
        style={{ touchAction: 'pan-y' }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={images[currentSlide]}
          alt={`${roomName} - Image ${currentSlide + 1}`}
          className="w-full h-full object-cover cursor-pointer transition-opacity duration-300"
          onClick={handleImageClick}
          loading="lazy"
          onError={(e) => {
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xNzUgMTI1SDIyNVYxNzVIMTc1VjEyNVoiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTIwMCAxNDBWMTYwSDIxMFYxNDBIMjAwWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
            e.target.alt = `Placeholder for ${roomName} - Image ${currentSlide + 1}`;
          }}
          onLoad={() => {
            // console.log(`Successfully loaded image ${currentSlide + 1} for ${roomName}`);
          }}
        />
        
        {/* Image overlay for better text visibility — pointer-events-none so taps reach the image (open lightbox) */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />

        {/* Scarcity / Last Room badge — real inventory only (room.totalroom); ≤5 shows urgency, 0/null hidden */}
        {roomsAvailable !== null && roomsAvailable > 0 && roomsAvailable <= 5 && (
          <div className="absolute top-2 left-2 bg-teal-500 text-white px-2 py-0.5 rounded text-[10px] leading-tight font-semibold shadow-md z-20 pointer-events-none">
            {roomsAvailable === 1 ? t('search.lastRoom') : t('search.lastRooms', { n: roomsAvailable })}
          </div>
        )}
      </div>

      {/* Navigation Controls */}
      {/* {showControls && images.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevSlide();
            }}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-95 text-gray-700 p-1.5 rounded-full shadow-md hover:bg-opacity-100 hover:scale-105 transition-all duration-200"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              nextSlide();
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-95 text-gray-700 p-1.5 rounded-full shadow-md hover:bg-opacity-100 hover:scale-105 transition-all duration-200"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </>
      )} */}

      {/* Image Counter */}
      {/* {images.length > 1 && (
        <div className="absolute bottom-3 left-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm z-10">
          {currentSlide + 1} / {images.length}
        </div>
      )} */}

      {/* Discount Badge */}
      {/* {discount && (
        <div className="absolute top-3 left-3 bg-red-500 text-white px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm z-10">
          {discount}
        </div>
      )} */}

      {/* Rating Badge */}
      {/* {rating && (
        <div className="absolute top-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full flex items-center space-x-1.5 shadow-sm z-10">
          <svg className="h-3.5 w-3.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span className="text-xs font-medium">{rating}</span>
        </div>
      )} */}

      {/* Pagination Dots */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-0 w-full flex justify-center space-x-2 z-10">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                goToSlide(index);
              }}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-200 cursor-pointer hover:scale-110 ${
                index === currentSlide 
                  ? 'bg-white shadow-sm scale-100' 
                  : 'bg-white bg-opacity-60 hover:bg-opacity-80'
              }`}
            />
          ))}
        </div>
      )}

      {/* Fullscreen Button */}
      {/* {onImageClick && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onImageClick();
          }}
          className="absolute bottom-3 right-3 bg-black bg-opacity-60 text-white p-1.5 rounded-full transition-all duration-200 hover:bg-opacity-80 hover:scale-105 z-10"
        >
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12z" />
          </svg>
        </button>
      )} */}
    </div>
  );
};

export default SimpleImageSlider;
